import { useCallback, useMemo, useEffect, useState } from 'react';
import { MoneyInput } from './money-input';
import { FormInputCommonProps } from './types';
import SelectArrow from '../../images/select-arrow.svg';
import { classNames } from '../../utils';
import { twMerge } from 'tailwind-merge';
import { get, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { LabelTooltip } from './input-row';
import { ResponsiveCostSlider } from './responsive-cost-slider';
import * as Popover from '@radix-ui/react-popover';

interface DropdownSliderInputProps extends FormInputCommonProps {
  value?: number | null | undefined;
  onChange: (value: number | undefined | null) => void;
  label: React.ReactNode;
  changeOnClose?: boolean;
  hideSelection?: boolean;
  tooltipText?: string;
  tooltipClassName?: string;
  iconClassName?: string;
  placeholder?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  labelClassName?: string;
  min: number;
  max: number;
  sliderHeaderLabel?: string;
  labelWrapperClassName?: string;
  openWrapperClassName?: string;
  selectArrowIcon?: React.ReactNode;
  selectArrowIconClassName?: string;
}

export const DropdownSliderInput: React.FC<DropdownSliderInputProps> = ({
  name,
  value,
  changeOnClose,
  onChange,
  label,
  disabled,
  className,
  tooltipText,
  tooltipClassName,
  iconClassName,
  hideSelection,
  dropdownClassName,
  labelClassName,
  min,
  max,
  sliderHeaderLabel,
  labelWrapperClassName,
  openWrapperClassName,
  selectArrowIcon,
  selectArrowIconClassName,
}) => {
  const methods = useFormContext();
  const errors = methods?.formState.errors;
  const error = methods && name ? get(errors, name) : null;

  const [localValue, setLocalValue] = useState<number | undefined | null>(
    value
  );

  const [moneyInputLocalValue, setMoneyInputLocalValue] = useState(value);

  const [open, openPopover] = useState(false);

  const onClose = useCallback(() => {
    if (changeOnClose && value !== localValue) {
      onChange(localValue);
    }
  }, [localValue, onChange]);

  useEffect(() => {
    setLocalValue(value);
  }, [value, setLocalValue]);

  return (
    <div className={twMerge('block w-full', className)}>
      <Popover.Root
        onOpenChange={(open: any) => {
          openPopover(open);
          if (!open) {
            onClose();
          }
        }}
      >
        <Popover.Trigger asChild>
          <div className="w-full">
            <div
              className={twMerge(
                'typo-body-small bg-hardwood-100 text-hardwood-700 hover:bg-hardwood-200 active:bg-hardwood-300 relative flex h-[28px] w-auto items-center overflow-hidden text-ellipsis rounded-[39px] pl-3 pr-[20px] text-left md:pl-[8px]',
                disabled
                  ? 'bg-hardwood-50 pointer-events-none cursor-default'
                  : '',
                hideSelection ? '' : 'lg:w-[250px]',
                labelWrapperClassName ? labelWrapperClassName : '',
                open && openWrapperClassName ? openWrapperClassName : ''
              )}
            >
              <div className="flex items-center">
                {tooltipText ? (
                  <LabelTooltip
                    tooltipClassName={tooltipClassName}
                    iconClassName={iconClassName}
                  >
                    {tooltipText}
                  </LabelTooltip>
                ) : null}
                <p className="mb-0 truncate lg:w-[225px]">
                  <span
                    className={classNames('mr-3 font-bold', labelClassName)}
                  >
                    {label}
                  </span>
                </p>
              </div>
              {selectArrowIcon ? (
                <div
                  className={classNames(
                    'absolute right-[6px] top-[14px] translate-y-[-50%]  transition-all',
                    open ? 'rotate-180' : '',
                    selectArrowIconClassName
                  )}
                >
                  {selectArrowIcon}
                </div>
              ) : (
                <img
                  alt="select-arrow"
                  className={classNames(
                    'absolute right-[10px] top-[50%] h-[6px] w-[10px] translate-y-[-50%]  transition-all',
                    open ? 'rotate-180' : ''
                  )}
                  src={SelectArrow}
                />
              )}
            </div>
          </div>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            sideOffset={0}
            align={'start'}
            avoidCollisions={false}
            asChild
            className="absolute z-[9999]"
          >
            <div
              className={classNames(
                'mt-[6px] flex max-h-[286px] w-auto flex-col gap-[6px] overflow-auto rounded-[4px] bg-white p-[16px] shadow-md md:w-[350px]',
                dropdownClassName
              )}
            >
              <ResponsiveCostSlider
                min={min}
                max={max}
                step={1}
                value={localValue || 0}
                valueDisplay={(val, isDragging) => {
                  let value;

                  if (val != null && isDragging) {
                    value = val;
                  } else {
                    value = localValue;
                  }

                  return (
                    <div className="flex flex-row items-center">
                      <MoneyInput
                        name={name}
                        value={value}
                        max={max}
                        inputClassName={'h-[24px] my-[4px] mx-0 w-[76px]'}
                        onBlur={(e) => {
                          setLocalValue(moneyInputLocalValue);
                          if (onChange) {
                            onChange(moneyInputLocalValue);
                          }
                        }}
                        onChange={(val) => {
                          setMoneyInputLocalValue(val);
                        }}
                      />
                      <div className="typo-body-small text-sidewalk-900">
                        {sliderHeaderLabel ? sliderHeaderLabel : ''}
                      </div>
                    </div>
                  );
                }}
                onChange={(val) => {
                  setLocalValue(val);
                  setMoneyInputLocalValue(val);
                  if (onChange) {
                    onChange(val);
                  }
                }}
                sliderClassName={'w-full order-last'}
                sliderValueWrapperClassName={'flex flex-col'}
                className="min-w-[300px] w-full"
                valueClassName={'w-full'}
              />
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {name && error ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => {
            return (
              <div className="typo-body-small text-hydrant-500 absolute">
                {message}
              </div>
            );
          }}
        />
      ) : null}
    </div>
  );
};
